import { useEffect, useState } from 'react';

type MediaQuerySpec = {
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  orientation?: 'portrait' | 'landscape';
};

const specToQuery = (spec: MediaQuerySpec): string => {
  const parts: string[] = [];
  if (spec.minWidth != null) parts.push(`(min-width: ${spec.minWidth}px)`);
  if (spec.maxWidth != null) parts.push(`(max-width: ${spec.maxWidth}px)`);
  if (spec.minHeight != null) parts.push(`(min-height: ${spec.minHeight}px)`);
  if (spec.maxHeight != null) parts.push(`(max-height: ${spec.maxHeight}px)`);
  if (spec.orientation) parts.push(`(orientation: ${spec.orientation})`);
  return parts.join(' and ') || 'all';
};

/**
 * Drop-in replacement for NativeBase's `useMediaQuery`, which Chakra v3
 * does not provide. Accepts a single spec (or an array of specs) using
 * min/max width/height and returns an array of booleans matching the
 * NB API: `const [isTall] = useMediaQuery({ minHeight: 830 })`.
 */
export const useMediaQuery = (
  query: MediaQuerySpec | MediaQuerySpec[]
): boolean[] => {
  const specs = Array.isArray(query) ? query : [query];
  const queries = specs.map(specToQuery);

  const [matches, setMatches] = useState<boolean[]>(() =>
    queries.map((q) =>
      typeof window !== 'undefined' && window.matchMedia
        ? window.matchMedia(q).matches
        : false
    )
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mqls = queries.map((q) => window.matchMedia(q));
    const update = () => setMatches(mqls.map((mql) => mql.matches));
    update();
    mqls.forEach((mql) => mql.addEventListener('change', update));
    return () =>
      mqls.forEach((mql) => mql.removeEventListener('change', update));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.join('|')]);

  return matches;
};
